import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import { OpenFeature } from '@openfeature/web-sdk';
import { reaction } from 'mobx';
import {
    flagsmith_external_proxy_hostname,
    flagsmith_client_side_environment_key,
} from './consts.js';
import { email, user_id } from './userData.js';
import { logEvent } from './log';
import { errorKind } from './log/clientEventSchema';

if (typeof window !== 'undefined') {
    if (!window.flagsmith_external_proxy_hostname || !window.flagsmith_client_side_environment_key) {
        window.flagsmith_external_proxy_hostname = flagsmith_external_proxy_hostname;
        window.flagsmith_client_side_environment_key = flagsmith_client_side_environment_key;
    }
}

let userContextReactionStarted = false;

function getIdentityTargetingKey() {
    const id = user_id.get();
    const mail = email.get();
    if (id != null && id !== '' && mail) {
        return `${id}:${mail}`;
    }
    return null;
}

function buildOpenFeatureContext(targetingKey) {
    const openFeatureContextOptions = { traits: {} };
    if (targetingKey !== null) {
        openFeatureContextOptions.targetingKey = targetingKey;
    }
    return openFeatureContextOptions;
}

async function applyOpenFeatureContext(targetingKey) {
    await OpenFeature.setContext(buildOpenFeatureContext(targetingKey));
}

export async function initializeFeatureFlags() {
    const flagsmithClientProvider = new FlagsmithClientProvider({
        api: `https://${window.flagsmith_external_proxy_hostname}/api/v1/`,
        environmentID: window.flagsmith_client_side_environment_key,
        cacheFlags: false,
        cacheOptions: {
            skipAPI: false,
        },
    });

    // Watch identity changes from the start: flags may load late (startFeatureFlags stops
    // waiting) and a sign-in or switch can happen meanwhile.
    if (!userContextReactionStarted) {
        userContextReactionStarted = true;
        reaction(
            () => getIdentityTargetingKey(),
            (targetingKey) => {
                void applyOpenFeatureContext(targetingKey);
            },
            { fireImmediately: false }
        );
    }

    const initialTargetingKey = getIdentityTargetingKey();
    await OpenFeature.setProviderAndWait(flagsmithClientProvider, buildOpenFeatureContext(initialTargetingKey));

    // Catch up with identity changes during the wait; the latest identity wins, even if it
    // changes again while one is being applied.
    let appliedTargetingKey = initialTargetingKey;
    for (let attempt = 0; attempt < 3 && getIdentityTargetingKey() !== appliedTargetingKey; attempt += 1) {
        appliedTargetingKey = getIdentityTargetingKey();
        await applyOpenFeatureContext(appliedTargetingKey);
    }
}

const FLAGS_TIMEOUT_MS = 10000;

// The app starts once flags have loaded, failed, or taken longer than timeoutMs: a Flagsmith
// outage used to leave the page blank. Flags then answer their defaults until they load.
export async function startFeatureFlags({ timeoutMs = FLAGS_TIMEOUT_MS } = {}) {
    let timer;
    try {
        await Promise.race([
            initializeFeatureFlags(),
            new Promise((resolve, reject) => {
                timer = setTimeout(() => reject(Object.assign(new Error('flags timeout'), { timedOut: true })), timeoutMs);
            }),
        ]);
    } catch (error) {
        logEvent('app.boot.failed', { stage: 'flags', error_kind: error && error.timedOut ? 'timeout' : errorKind(error) });
    } finally {
        clearTimeout(timer);
    }
}

export async function getFlagValue(flagKey, defaultValue = false) {
    const client = await OpenFeature.getClient();
    return client.getBooleanValue(flagKey, defaultValue);
}

export async function getObjectFlagValue(flagKey, defaultValue = null) {
    const client = await OpenFeature.getClient();
    return client.getObjectValue(flagKey, defaultValue);
}
